import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fmtDate } from "@/lib/api";
import { ROLE_LABELS, useAuth } from "@/context/AuthContext";
import { usePlant } from "@/context/PlantContext";
import { PageHeader, Panel, StatusChip } from "@/components/common";

const EMPTY = { email: "", name: "", role: "viewer", plants: [], password: "" };

function UserDialog({ user, onClose, onSaved }) {
  const { plants } = usePlant();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) setF({ ...EMPTY, ...user, password: "" }); }, [user]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const toggle = (c) => setF((x) => ({ ...x, plants: x.plants.includes(c) ? x.plants.filter((p) => p !== c) : [...x.plants, c] }));
  const save = async () => {
    setBusy(true);
    try {
      if (user.id) {
        const body = { name: f.name, role: f.role, plants: f.plants };
        if (f.password) body.password = f.password;
        await api.put(`/users/${user.id}`, body);
      } else {
        await api.post("/users", { email: f.email, name: f.name, role: f.role, plants: f.plants, password: f.password });
      }
      toast.success("User saved");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader><DialogTitle>{user?.id ? `Edit ${user.email}` : "New user"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</label>
            <Input data-testid="user-email-input" type="email" disabled={!!user?.id} value={f.email} onChange={set("email")} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Name</label>
            <Input data-testid="user-name-input" value={f.name} onChange={set("name")} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Role</label>
            <Select value={f.role} onValueChange={set("role")}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`user-role-option-${k}`}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {f.role === "purchasing" && (
            <div className="col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Assigned plants</label>
              <div className="mt-2 grid grid-cols-5 gap-2" data-testid="user-plants-picker">
                {plants.map((p) => (
                  <label key={p.code} className="flex items-center gap-1.5 rounded-md border border-slate-200 px-2 py-1.5 text-xs">
                    <Checkbox checked={f.plants.includes(p.code)} onCheckedChange={() => toggle(p.code)} data-testid={`user-plant-${p.code}`} /> {p.code}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{user?.id ? "New password (optional)" : "Password (min 8 chars)"}</label>
            <Input data-testid="user-password-input" type="password" value={f.password} onChange={set("password")} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="user-save-btn" onClick={save} disabled={busy}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save user</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="users-page">
      <PageHeader title="Users" subtitle="Admin, Compliance, Purchasing / Plant (limited to assigned plants) and read-only Viewer.">
        <Button data-testid="user-add-btn" onClick={() => setEdit({})}><Plus className="mr-1.5 h-4 w-4" /> New user</Button>
      </PageHeader>
      <Panel>
        <div className="-m-5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>{["Name", "Email", "Role", "Plants", "Status", "Last login", ""].map((h) => <th key={h} className="px-5 py-2.5 text-left">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`}>
                  <td className="px-5 py-3 font-medium text-slate-900">{u.name}</td>
                  <td className="px-5 py-3">{u.email}</td>
                  <td className="px-5 py-3"><span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium">{ROLE_LABELS[u.role]}</span></td>
                  <td className="px-5 py-3 font-mono text-xs">{u.role === "purchasing" ? u.plants?.join(", ") || "none" : "all"}</td>
                  <td className="px-5 py-3"><StatusChip status={u.active === false ? "revoked" : "active"} label={u.active === false ? "inactive" : "active"} /></td>
                  <td className="px-5 py-3 text-xs text-slate-500">{fmtDate(u.last_login_at, true)}</td>
                  <td className="whitespace-nowrap px-5 py-3 text-right">
                    <button data-testid={`user-edit-${u.email}`} onClick={() => setEdit(u)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`user-reset-tour-${u.email}`} title="Reset tour (starts at next login)" onClick={() => api.post(`/users/${u.id}/reset-tour`).then(() => { toast.success(`Tour reset for ${u.email} – it starts at their next login`); load(); })} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><RotateCcw className="h-4 w-4" /></button>
                    {u.id !== me.id && <button data-testid={`user-delete-${u.email}`} onClick={() => del(u)} className="rounded p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      <UserDialog user={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
